import { useEffect, useRef, useState } from "react";
import { MessageCircle, Send, X, Sparkles } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const SID_KEY = "pc_chat_sid";

const getSid = () => {
  let s = localStorage.getItem(SID_KEY);
  if (!s) {
    s = (crypto.randomUUID && crypto.randomUUID()) || `s-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem(SID_KEY, s);
  }
  return s;
};

const GREETING = {
  role: "assistant",
  content:
    "Namaskara! I'm Sahayaka. Ask me about booking a purohith, group parayana, astrology, stotras or festivals. ಕನ್ನಡದಲ್ಲಿಯೂ ಕೇಳಬಹುದು.",
};

const QUICK = ["Book a parayana", "Astrology readings", "Griha Pravesha pooja", "Navaratri 2026"];

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [ping, setPing] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    if (localStorage.getItem("pc_chat_seen")) return;
    const t = setTimeout(() => setPing(true), 3500);
    return () => clearTimeout(t);
  }, []);

  const openChat = () => {
    setOpen((o) => !o);
    setPing(false);
    localStorage.setItem("pc_chat_seen", "1");
  };

  useEffect(() => {
    if (open) endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open, loading]);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: q }]);
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: getSid(),
          message: q,
          page: window.location.pathname,
          referrer: document.referrer || null,
          user_agent: navigator.userAgent,
        }),
      });
      const data = await res.json();
      setMessages((m) => [...m, { role: "assistant", content: data.reply || "Sorry, please try again." }]);
    } catch {
      setMessages((m) => [
        ...m,
        { role: "assistant", content: "I couldn't reach the server. Please try again, or WhatsApp us on 9110248636." },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{`
        @keyframes pcSlideUp { from { opacity:0; transform: translateY(18px) scale(.98); } to { opacity:1; transform:none; } }
        .pc-slide-up { animation: pcSlideUp .28s cubic-bezier(.2,.8,.2,1); }
        @keyframes pcPing { 0% { transform: scale(1); opacity:.5; } 70%,100% { transform: scale(1.8); opacity:0; } }
        .pc-ping { animation: pcPing 2s ease-out infinite; }
      `}</style>

      {ping && !open && (
        <button
          onClick={openChat}
          data-testid="chat-greet-bubble"
          className="fixed bottom-[86px] right-5 z-[80] max-w-[220px] rounded-2xl rounded-br-sm border border-[#A8722A]/25 bg-[#FFFDF8] px-4 py-2.5 text-left font-mukta text-sm text-[#3B221B] shadow-xl pc-slide-up"
        >
          <span className="font-semibold text-[#B23A2B]">Namaskara!</span> Ask me about pooja, parayana or astrology.
        </button>
      )}

      <button
        onClick={openChat}
        data-testid="chat-toggle-btn"
        aria-label={open ? "Close chat" : "Open chat assistant"}
        className="fixed bottom-5 right-5 z-[80] flex h-14 w-14 items-center justify-center rounded-full bg-[#B23A2B] text-[#FFFDF8] shadow-lg transition-transform hover:scale-105 hover:bg-[#9a2f22]"
      >
        {ping && !open && <span className="pc-ping absolute inset-0 rounded-full bg-[#B23A2B]" aria-hidden="true" />}
        <span className="relative">{open ? <X size={24} /> : <MessageCircle size={26} />}</span>
      </button>

      {open && (
        <div
          data-testid="chat-panel"
          className="fixed bottom-24 right-5 z-[80] flex h-[70vh] max-h-[560px] w-[92vw] max-w-[380px] flex-col overflow-hidden rounded-[18px] border border-[#A8722A]/30 bg-[#FBF6EC] shadow-2xl pc-slide-up"
        >
          <div className="flex items-center gap-2.5 bg-[#3B221B] px-4 py-3 text-[#FFFDF8]">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#B23A2B]"><Sparkles size={17} /></span>
            <div>
              <p className="font-eczar text-sm font-bold leading-tight">Sahayaka</p>
              <p className="font-mukta text-[11px] text-[#FFFDF8]/70">PurohithConnect™ assistant</p>
            </div>
          </div>

          <div className="flex-1 space-y-3 overflow-y-auto px-3.5 py-4" data-testid="chat-messages">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${m.role}`}>
                <div
                  className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-3.5 py-2 font-mukta text-sm ${
                    m.role === "user"
                      ? "rounded-br-sm bg-[#B23A2B] text-[#FFFDF8]"
                      : "rounded-bl-sm border border-[#A8722A]/25 bg-[#FFFDF8] text-[#3B221B]"
                  }`}
                >
                  {m.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start" data-testid="chat-typing">
                <div className="rounded-2xl rounded-bl-sm border border-[#A8722A]/25 bg-[#FFFDF8] px-4 py-2.5">
                  <span className="flex gap-1">
                    <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-[#A8722A] [animation-delay:-0.3s]" />
                    <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-[#A8722A] [animation-delay:-0.15s]" />
                    <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-[#A8722A]" />
                  </span>
                </div>
              </div>
            )}
            {messages.length <= 1 && (
              <div className="flex flex-wrap gap-2 pt-1">
                {QUICK.map((q) => (
                  <button key={q} onClick={() => send(q)} data-testid="chat-quick-prompt"
                    className="rounded-full border border-[#A8722A]/40 bg-[#FFFDF8] px-3 py-1 font-mukta text-xs text-[#6B5044] transition-colors hover:bg-[#F3EADA]">
                    {q}
                  </button>
                ))}
              </div>
            )}
            <div ref={endRef} />
          </div>

          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="flex items-center gap-2 border-t border-[#A8722A]/20 bg-[#FBF6EC] p-2.5"
          >
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              data-testid="chat-input"
              placeholder="Ask about pooja, parayana, astrology…"
              className="flex-1 rounded-full border border-[#A8722A]/30 bg-white px-4 py-2 font-mukta text-sm text-[#3B221B] outline-none focus:border-[#A8722A] focus:ring-2 focus:ring-[#A8722A]/30"
            />
            <button type="submit" disabled={loading} data-testid="chat-send-btn"
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#3E6B4F] text-white transition-colors hover:bg-[#2c5039] disabled:opacity-50">
              <Send size={16} />
            </button>
          </form>
        </div>
      )}
    </>
  );
};
